import React, { useEffect, useMemo, useState, useCallback } from "react";
import Moment from "react-moment";
import { useDispatch, useSelector } from "react-redux";
import {
  getCurrentUser,
  getLoggedUserDetails,
  loadUserDetails,
} from "../../app/store/auth";
import {
  useLoadCandidateAppsQuery,
  useLoadAppRuningTasksQuery,
  useLoadAppDocumentsQuery,
  useLoadCandidatesByFilterMutation,
} from "../../app/store/candidateApiSlice";
import { getFieldData, getFormatedNumber } from "../../Services/appService";
import { setCurrentPageTitle, setCurrentRoute } from "../../app/store/layoutUI";
import useDocDownload from "../../app/hooks/useDocDownload";

const ROUTE_ONBOARDING = [
  { name: "Task Management", url: "" },
  { name: "Onboarding Steps", url: "" },
];

const DOC_ICON_BY_EXTENSION = {
  pdf: "fa-file-pdf-o",
  doc: "fa-file-word-o", docx: "fa-file-word-o", rtf: "fa-file-word-o",
  xls: "fa-file-excel-o", xlsx: "fa-file-excel-o", csv: "fa-file-excel-o",
  ppt: "fa-file-powerpoint-o", pptx: "fa-file-powerpoint-o",
  png: "fa-file-image-o", jpg: "fa-file-image-o", jpeg: "fa-file-image-o", gif: "fa-file-image-o",
  zip: "fa-file-archive-o", rar: "fa-file-archive-o",
  txt: "fa-file-text-o", log: "fa-file-text-o"
};

const getDocumentIcon = (doc) => {
  const name = String(doc?.docName || doc?.documentName || doc || "").trim().toLowerCase();
  const ext = name.includes(".") ? name.slice(name.lastIndexOf(".") + 1) : "";
  if (DOC_ICON_BY_EXTENSION[ext]) return DOC_ICON_BY_EXTENSION[ext];
  return "fa-file-text-o";
};

const getDocumentIconStyle = (doc) => {
  const icon = getDocumentIcon(doc);
  if (icon === "fa-file-pdf-o") return { color: "#dc2626" };
  if (icon === "fa-file-word-o") return { color: "#000000" };
  if (icon === "fa-file-excel-o") return { color: "#16a34a" };
  if (icon === "fa-file-image-o") return { color: "#9333ea" };
  return { color: "#000000" };
};

const REPORTING_STYLES = {
  card: {
    borderRadius: "0",
    border: "1px solid #e5e5e5",
    backgroundColor: "#ffffff",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.04)",
    overflow: "hidden"
  },
  cardHeader: {
    padding: "18px 22px",
    borderBottom: "1px solid #e5e5e5",
    background: "#ffffff"
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    flexWrap: "wrap"
  },
  titleWrap: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    minWidth: 0
  },
  titleIcon: {
    width: "34px",
    height: "34px",
    borderRadius: "0",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f1f5f9",
    color: "#0f172a",
    fontSize: "14px",
    border: "1px solid #e5e5e5",
    boxShadow: "none",
    flexShrink: 0
  },
  title: {
    fontSize: "18px",
    fontWeight: 600,
    lineHeight: 1.25,
    color: "#000000",
    margin: 0
  },
  subtitle: {
        fontSize: "13px",
        lineHeight: 1.5,
        color: "#666666",
        margin: 0
    },
  refreshButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    background: "transparent",
    border: "none",
    color: "#2563eb",
    padding: "0",
    fontSize: "13px",
    fontWeight: 500,
    cursor: "pointer",
    textDecoration: "none"
  },
  cardBody: {
    padding: "20px 22px"
  },
  statsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "14px",
    marginBottom: "20px"
  },
  statCard: {
    display: "flex",
    alignItems: "flex-start",
    gap: "12px",
    backgroundColor: "#ffffff",
    border: "1px solid #e5e5e5",
    borderRadius: "0",
    padding: "16px",
    boxShadow: "0 6px 18px rgba(0, 0, 0, 0.03)",
    transition: "all 0.2s ease"
  },
  statIcon: {
    width: "36px",
    height: "36px",
    borderRadius: "0",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f1f5f9",
    color: "#0f172a",
    fontSize: "14px",
    flexShrink: 0
  },
  statContent: {
    minWidth: 0
  },
  statLabel: {
    margin: "0 0 4px 0",
    color: "#666666",
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase"
  },
  statValue: {
    margin: 0,
    color: "#000000",
    fontSize: "22px",
    fontWeight: 600,
    lineHeight: 1.2
  },
  sectionGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "16px",
    marginBottom: "16px"
  },
  innerCard: {
    borderRadius: "0",
    border: "1px solid #e5e5e5",
    backgroundColor: "#ffffff",
    boxShadow: "0 6px 18px rgba(0, 0, 0, 0.03)",
    overflow: "hidden",
    height: "100%"
  },
  innerCardHeader: {
    padding: "16px 20px",
    borderBottom: "1px solid #e5e5e5",
    background: "#f5f5f5"
  },
  innerTitleWrap: {
    display: "flex",
    alignItems: "center",
    gap: "10px"
  },
  innerTitleIcon: {
    width: "32px",
    height: "32px",
    borderRadius: "0",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f1f5f9",
    color: "#0f172a",
    fontSize: "13px",
    border: "1px solid #e5e5e5",
    boxShadow: "none",
    flexShrink: 0
  },
  innerTitle: {
    fontSize: "15px",
    fontWeight: 600,
    color: "#000000",
    margin: 0
  },
  innerSubtitle: {
    fontSize: "13px",
    lineHeight: 1.5,
    color: "#666666",
    margin: 0
  },
  innerCardBody: {
    padding: "18px 20px"
  },
  compactDetailGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "10px"
  },
  compactDetailRow: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    padding: "12px 14px",
    border: "1px solid #e5e5e5",
    borderRadius: "0",
    background: "#ffffff",
    boxShadow: "0 2px 6px rgba(15, 23, 42, 0.02)"
  },
  detailLabel: {
    fontSize: "11px",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#666666"
  },
  compactDetailValue: {
    fontSize: "13.5px",
    lineHeight: 1.45,
    color: "#000000",
    fontWeight: 600,
    wordBreak: "break-word"
  },
  docList: {
    display: "flex",
    flexDirection: "column",
    gap: "8px"
  },
  docButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    width: "fit-content",
    padding: "9px 18px",
    borderRadius: "0",
    border: "1px solid #e5e5e5",
    background: "#ffffff",
    color: "#333333",
    fontSize: "13px",
    fontWeight: 600,
    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.05)",
    cursor: "pointer",
    transition: "all 0.2s ease"
  },
  emptyState: {
    fontSize: "13px",
    color: "#666666",
    margin: 0,
    fontWeight: 600
  }
};

export default function ReportingDetails() {
  const userDetailsSelector = useSelector(getLoggedUserDetails);
  const [handleDownloadFile] = useDocDownload();

  const dispatch = useDispatch();
  const currentUser = useMemo(() => getCurrentUser(), []);
  const currentUserId = currentUser?.id;

  useEffect(() => {
    dispatch(setCurrentRoute(ROUTE_ONBOARDING));
  }, [dispatch]);

  useEffect(() => {
    if (!userDetailsSelector && currentUserId) {
      dispatch(loadUserDetails(currentUserId));
    }
  }, [dispatch, userDetailsSelector, currentUserId]);

  const [loadCandidatesByFilter, {
    data: candidateSelector = [],
    isLoading: isCandidateLoading,
    isUninitialized: isCandidateUninitialized,
  }] = useLoadCandidatesByFilterMutation();

  useEffect(() => {
    if (currentUserId > 0) {
      const frmBody = new FormData();
      frmBody.append("id", currentUserId);
      frmBody.append("name", "");
      frmBody.append("email", "");
      frmBody.append("entity", "");
      frmBody.append("type", "candidate");
      frmBody.append("filterType", "single");
      loadCandidatesByFilter(frmBody);
    }
  }, [currentUserId, loadCandidatesByFilter]);

  const candidate = useMemo(() => {
    return candidateSelector[0] || null;
  }, [candidateSelector]);

  useEffect(() => {
    let entType = "";
    if (candidate) {
      entType = getFieldData(
        candidate.performerData,
        "candidateonboardingform",
        "CandidateEntity"
      );
    } else if (userDetailsSelector) {
      entType = getFieldData(
        userDetailsSelector.performerData,
        "candidateonboardingform",
        "CandidateEntity"
      );
    }

    if (entType) {
      const count = entType !== "C2C Basis" ? "5" : "4";
      dispatch(setCurrentPageTitle(`STEP-${count} : Reporting Details`));
    }
  }, [candidate, userDetailsSelector, dispatch]);

  const {
    data: candidateApps = [],
    isLoading: isAppsLoading,
    isFetching: isAppsFetching,
    refetch: refetchCandidateApps,
  } = useLoadCandidateAppsQuery(currentUserId, { skip: !currentUserId });

  const wfId = useMemo(() => {
    if (!candidateApps || candidateApps.length === 0) return 0;
    const mainWorkflow = candidateApps.filter((x) => x.workflowType === 1);
    return mainWorkflow[0]?.oldWorkflowId || 0;
  }, [candidateApps]);

  const {
    data: appTasks = [],
    isLoading: isTasksLoading,
    isFetching: isTasksFetching,
    refetch: refetchAppTasks,
  } = useLoadAppRuningTasksQuery(
    { wfId, userId: currentUserId },
    { skip: !wfId || !currentUserId }
  );

  const {
    data: appDocuments = [],
    isLoading: isDocumentsLoading,
    isFetching: isDocumentsFetching,
    refetch: refetchAppDocuments,
  } = useLoadAppDocumentsQuery(
    { wfId, userId: currentUserId },
    { skip: !wfId || !currentUserId }
  );

  const wfTask = useMemo(() => {
    if (!appTasks || appTasks.length === 0) return null;
    const filterTasks = appTasks.filter((x) => x.taskType === 1 && x.wiid > 0);
    return filterTasks[0] || null;
  }, [appTasks]);

  const rptDocs = useMemo(() => {
    if (!appDocuments || appDocuments.length === 0) return [];
    return appDocuments.filter(
      (x) => x.uploadType === "reporting" && x.sendStatus === true && x.deleteFlag !== true
    );
  }, [appDocuments]);

  const isInitialLoading =
    (Boolean(currentUserId) && isCandidateUninitialized) ||
    isCandidateLoading || isAppsLoading || isTasksLoading || isDocumentsLoading;
  const isDataFetching = isAppsFetching || isTasksFetching || isDocumentsFetching;
  const hasReportingData = Boolean(candidate || wfTask || rptDocs.length > 0);
  const isDataLoading = isInitialLoading || (isDataFetching && !hasReportingData);

  const handleDocumentLoad = useCallback(() => {
    if (currentUserId > 0) {
      refetchCandidateApps();
      refetchAppTasks();
      refetchAppDocuments();
    }
  }, [currentUserId, refetchCandidateApps, refetchAppTasks, refetchAppDocuments]);

  const item = candidate || null;
  const agencyName = useMemo(() => {
    return item && getFieldData(item.performerData, "projectengagement", "AgencyName");
  }, [item]);
  const positionTitle = useMemo(() => {
    return item && getFieldData(item.performerData, "projectengagement", "PositionTitle");
  }, [item]);

  const reportingInfo = useMemo(
    () => [
      {
        label: "Position Title",
        value: positionTitle,
      },
      {
        label: "Reporting Time",
        value:
          wfTask &&
          getFieldData(wfTask.stateData, "reportingdetails", "ReportingTime"),
      },
      {
        label: "Dress Code",
        value: wfTask && getFieldData(wfTask.stateData, "reportingdetails", "DressCode"),
      },
      {
        label: "Parking",
        value: wfTask && getFieldData(wfTask.stateData, "reportingdetails", "Parking"),
      },
      {
        label: "Notes",
        value: wfTask && getFieldData(wfTask.stateData, "reportingdetails", "Notes"),
      },
    ],
    [wfTask, positionTitle]
  );

  const workLocationInfo = useMemo(
    () => [
      {
        label: "Address Line 1",
        value:
          wfTask && getFieldData(wfTask.stateData, "worklocationaddress", "AddressLine1"),
      },
      {
        label: "Address Line 2",
        value:
          wfTask && getFieldData(wfTask.stateData, "worklocationaddress", "AddressLine2"),
      },
      {
        label: "City",
        value: wfTask && getFieldData(wfTask.stateData, "worklocationaddress", "City"),
      },
      {
        label: "State",
        value: wfTask && getFieldData(wfTask.stateData, "worklocationaddress", "State"),
      },
      {
        label: "Zip Code",
        value: wfTask && getFieldData(wfTask.stateData, "worklocationaddress", "Zip"),
      },
    ],
    [wfTask]
  );

  const reportingToInfo = useMemo(
    () => [
      {
        label: "Name",
        value: wfTask && getFieldData(wfTask.stateData, "reportingto", "Name"),
      },
      {
        label: "Title",
        value: wfTask && getFieldData(wfTask.stateData, "reportingto", "Title"),
      },
      {
        label: "Phone",
        value:
          wfTask &&
          getFormatedNumber(getFieldData(wfTask.stateData, "reportingto", "Phone")),
      },
      {
        label: "Email",
        value: wfTask && getFieldData(wfTask.stateData, "reportingto", "Email"),
      },
    ],
    [wfTask]
  );

  const summary = useMemo(() => {
    return {
      agency: agencyName || "--",
      startDate:
        item && item.duration?.startDate ? (
          <Moment format="MMM Do, YYYY">{item.duration.startDate}</Moment>
        ) : (
          "--"
        ),
      endDate:
        item && item.duration?.endDate ? (
          <Moment format="MMM Do, YYYY">{item.duration.endDate}</Moment>
        ) : (
          "--"
        ),
      documents: rptDocs.length,
      contact:
        (wfTask && getFieldData(wfTask.stateData, "reportingto", "Name")) || "--",
    };
  }, [agencyName, item, rptDocs.length, wfTask]);

  const renderCompactDetailRows = useCallback((rows) => (
    <div style={REPORTING_STYLES.compactDetailGrid}>
      {rows.map((row) => (
        <div key={row.label} style={REPORTING_STYLES.compactDetailRow}>
          <div style={REPORTING_STYLES.detailLabel}>{row.label}</div>
          <div style={REPORTING_STYLES.compactDetailValue}>{row.value || "--"}</div>
        </div>
      ))}
    </div>
  ), []);

  return (
    <React.Fragment>
      <div className="form-group mt-2">
        <div style={REPORTING_STYLES.card}>
          <div style={REPORTING_STYLES.cardHeader}>
            <div style={REPORTING_STYLES.headerRow}>
              <div style={REPORTING_STYLES.titleWrap}>
                <span style={REPORTING_STYLES.titleIcon}>
                  <i className="fa fa-map-marker" aria-hidden="true"></i>
                </span>
                <div>
                  <h1 style={REPORTING_STYLES.title}>Reporting Details</h1>
                  <p style={REPORTING_STYLES.subtitle}>
                    Review location, schedule, documents, and contact details for your reporting onboarding step.
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="btn btn-sm doc-link-btn"
                style={REPORTING_STYLES.refreshButton}
                onClick={handleDocumentLoad}
                disabled={isDataFetching}
                title="Refresh Reporting Details"
              >
                <i className={`fa fa-refresh ${isDataFetching ? "fa-spin" : ""}`} aria-hidden="true"></i>{" "}
                {isDataFetching ? "Fetching..." : "Refresh"}
              </button>
            </div>
          </div>

          <div style={REPORTING_STYLES.cardBody}>
            {isDataFetching && hasReportingData && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 18px', margin: '-20px -22px 20px', background: '#eff6ff', borderTop: '1px solid #bfdbfe', borderBottom: '1px solid #bfdbfe', color: '#1e40af', fontSize: '12px', fontWeight: 600 }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                  <i className='fa fa-circle-o-notch fa-spin' style={{ color: '#2563eb', fontSize: '13px' }}></i>
                  <span>Fetching updated reporting details...</span>
                </div>
                <span style={{ fontSize: '11px', color: '#3b82f6', fontWeight: 500 }}>Updating</span>
              </div>
            )}
            {isDataLoading && <div className='text-center p-5'><i className='fa fa-circle-o-notch fa-spin'></i><div>Loading reporting details...</div></div>}
            {!isDataLoading && (
              <div style={{ opacity: isDataFetching && hasReportingData ? 0.7 : 1, transition: 'opacity 0.2s ease' }}>
            <div style={REPORTING_STYLES.statsRow}>
              <div style={REPORTING_STYLES.statCard}>
                <div style={REPORTING_STYLES.statIcon}>
                  <i className="fa fa-building-o" aria-hidden="true"></i>
                </div>
                <div style={REPORTING_STYLES.statContent}>
                  <div style={REPORTING_STYLES.statLabel}>Agency</div>
                  <div style={REPORTING_STYLES.statValue}>{summary.agency}</div>
                </div>
              </div>
              <div style={REPORTING_STYLES.statCard}>
                <div style={REPORTING_STYLES.statIcon}>
                  <i className="fa fa-calendar-check-o" aria-hidden="true"></i>
                </div>
                <div style={REPORTING_STYLES.statContent}>
                  <div style={REPORTING_STYLES.statLabel}>Start Date</div>
                  <div style={REPORTING_STYLES.statValue}>{summary.startDate}</div>
                </div>
              </div>
              <div style={REPORTING_STYLES.statCard}>
                <div style={REPORTING_STYLES.statIcon}>
                  <i className="fa fa-calendar-times-o" aria-hidden="true"></i>
                </div>
                <div style={REPORTING_STYLES.statContent}>
                  <div style={REPORTING_STYLES.statLabel}>End Date</div>
                  <div style={REPORTING_STYLES.statValue}>{summary.endDate}</div>
                </div>
              </div>
              <div style={REPORTING_STYLES.statCard}>
                <div style={REPORTING_STYLES.statIcon}>
                  <i className="fa fa-file-text-o" aria-hidden="true"></i>
                </div>
                <div style={REPORTING_STYLES.statContent}>
                  <div style={REPORTING_STYLES.statLabel}>Documents</div>
                  <div style={REPORTING_STYLES.statValue}>{summary.documents}</div>
                </div>
              </div>
              <div style={REPORTING_STYLES.statCard}>
                <div style={REPORTING_STYLES.statIcon}>
                  <i className="fa fa-user-circle-o" aria-hidden="true"></i>
                </div>
                <div style={REPORTING_STYLES.statContent}>
                  <div style={REPORTING_STYLES.statLabel}>Reporting To</div>
                  <div style={REPORTING_STYLES.statValue}>{summary.contact}</div>
                </div>
              </div>
            </div>

            <div style={REPORTING_STYLES.sectionGrid}>
              <div style={REPORTING_STYLES.innerCard}>
                <div style={REPORTING_STYLES.innerCardHeader}>
                  <div style={REPORTING_STYLES.innerTitleWrap}>
                    <span style={REPORTING_STYLES.innerTitleIcon}>
                      <i className="fa fa-folder-open-o" aria-hidden="true"></i>
                    </span>
                    <div>
                      <h3 style={REPORTING_STYLES.innerTitle}>Documents</h3>
                      <p style={REPORTING_STYLES.innerSubtitle}>Files shared for reporting and day-one prep</p>
                    </div>
                  </div>
                </div>
                <div style={REPORTING_STYLES.innerCardBody}>
                  {rptDocs.length > 0 ? (
                    <div style={REPORTING_STYLES.docList}>
                      {rptDocs.map((doc) => (
                        <button
                          key={doc.id}
                          type="button"
                          className="btn btn-sm"
                          style={REPORTING_STYLES.docButton}
                          title="Download File"
                          onClick={() => handleDownloadFile(doc)}
                        >
                          <i className={`fa ${getDocumentIcon(doc)}`} aria-hidden="true" style={getDocumentIconStyle(doc)}></i>
                          <span>{doc.documentName}</span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p style={REPORTING_STYLES.emptyState}>
                      No reporting documents are available yet.
                    </p>
                  )}
                </div>
              </div>

              <div style={REPORTING_STYLES.innerCard}>
                <div style={REPORTING_STYLES.innerCardHeader}>
                  <div style={REPORTING_STYLES.innerTitleWrap}>
                    <span style={REPORTING_STYLES.innerTitleIcon}>
                      <i className="fa fa-briefcase" aria-hidden="true"></i>
                    </span>
                    <div>
                      <h3 style={REPORTING_STYLES.innerTitle}>Reporting Details</h3>
                      <p style={REPORTING_STYLES.innerSubtitle}>Role, schedule, and reporting expectations</p>
                    </div>
                  </div>
                </div>
                <div style={REPORTING_STYLES.innerCardBody}>
                  {renderCompactDetailRows(reportingInfo)}
                </div>
              </div>

              <div style={REPORTING_STYLES.innerCard}>
                <div style={REPORTING_STYLES.innerCardHeader}>
                  <div style={REPORTING_STYLES.innerTitleWrap}>
                    <span style={REPORTING_STYLES.innerTitleIcon}>
                      <i className="fa fa-map-marker" aria-hidden="true"></i>
                    </span>
                    <div>
                      <h3 style={REPORTING_STYLES.innerTitle}>Customer Address</h3>
                      <p style={REPORTING_STYLES.innerSubtitle}>Where you are expected to report on-site</p>
                    </div>
                  </div>
                </div>
                <div style={REPORTING_STYLES.innerCardBody}>
                  {renderCompactDetailRows(workLocationInfo)}
                </div>
              </div>

              <div style={REPORTING_STYLES.innerCard}>
                <div style={REPORTING_STYLES.innerCardHeader}>
                  <div style={REPORTING_STYLES.innerTitleWrap}>
                    <span style={REPORTING_STYLES.innerTitleIcon}>
                      <i className="fa fa-user-circle-o" aria-hidden="true"></i>
                    </span>
                    <div>
                      <h3 style={REPORTING_STYLES.innerTitle}>Reporting To</h3>
                      <p style={REPORTING_STYLES.innerSubtitle}>Primary person to contact for work coordination</p>
                    </div>
                  </div>
                </div>
                <div style={REPORTING_STYLES.innerCardBody}>
                  {renderCompactDetailRows(reportingToInfo)}
                </div>
              </div>
            </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}
